import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Check } from 'lucide-react';
import { FACT_ROWS, LENDERS, financingMicrocopy, lenderHref, FINANCING_DISCLOSURE } from '../../config/financing';
import { trackFinancingClick } from '../../lib/analytics';

/* Shared lender UI — every financing button pair, lender card, comparison table and disclosure on the site
   renders from config/financing.js LENDERS. Nothing here names a lender; layouts handle 2–4 entries. */

const cols = (n) => ({ 2: 'sm:grid-cols-2', 3: 'sm:grid-cols-3', 4: 'sm:grid-cols-2 lg:grid-cols-4' }[Math.min(Math.max(n, 2), 4)] || 'sm:grid-cols-2');

const BTN = {
  // The two tones alternate so the pair reads as equal options (never one primary, one secondary).
  light: [
    'bg-[#3E6F8E] hover:bg-[#355F79] text-white',
    'bg-[#121417] hover:bg-[#1B2026] text-white',
    'bg-[#2F6B57] hover:bg-[#285B4A] text-white',
    'bg-[#8A5A2B] hover:bg-[#744A22] text-white',
  ],
  dark: [
    'idb-btn-copper text-[#121417]',
    'border border-[rgba(244,241,234,0.32)] text-white hover:border-[rgba(244,241,234,0.6)] hover:bg-[rgba(255,252,246,0.06)]',
    'bg-[#3E6F8E] hover:bg-[#355F79] text-white',
    'bg-[#2F6B57] hover:bg-[#285B4A] text-white',
  ],
};

/**
 * The button pair (or trio / quartet). Always renders EVERY lender — side by side from `sm`, stacked full-width
 * below. `amount` is appended only to lenders that take a loan-amount parameter (Acorn); HFS stays untouched.
 */
export const LenderButtons = ({ lenders = LENDERS, amount = null, placement = 'page', dark = false, microcopy = true, compact = false, className = '', onClick = null, testId = 'lender-buttons' }) => {
  if (!lenders || lenders.length === 0) return null;
  const h = compact ? 'h-11' : 'h-12';
  return (
    <div className={className} data-testid={testId} data-placement={placement} data-lender-count={lenders.length}>
      <div className={`grid grid-cols-1 ${cols(lenders.length)} gap-3`}>
        {lenders.map((l, i) => (
          <a
            key={l.id}
            href={lenderHref(l, amount)}
            target="_blank"
            rel="noopener sponsored"
            onClick={() => { trackFinancingClick(l.id, placement); if (onClick) onClick(l); }}
            data-testid={`lender-button-${l.id}`}
            data-lender={l.id}
            className={`${h} w-full px-4 inline-flex items-center justify-center gap-2 rounded-[10px] text-sm font-semibold transition-colors active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3E6F8E] focus-visible:ring-offset-2 ${(dark ? BTN.dark : BTN.light)[i % 4]}`}
          >
            <span className="truncate">{l.buttonLabel}</span> <ArrowUpRight className="h-4 w-4 shrink-0 opacity-80" aria-hidden="true" />
          </a>
        ))}
      </div>
      {microcopy && (
        <p className={`mt-2 text-xs leading-relaxed ${dark ? 'text-[#8FA0AE]' : 'text-[#61676E]'}`} data-testid={`${testId}-microcopy`}>{financingMicrocopy(lenders)}</p>
      )}
    </div>
  );
};

/** One equal card per lender: name, amount range, pitch, three bullets, its own button. */
export const LenderCards = ({ lenders = LENDERS, placement = 'lenders', testId = 'lender-cards' }) => (
  <div className={`grid grid-cols-1 ${cols(lenders.length)} gap-5`} data-testid={testId} data-lender-count={lenders.length}>
    {lenders.map((l, i) => (
      <article key={l.id} className="idb-card idb-brackets p-6 flex flex-col" data-testid={`lender-card-${l.id}`}>
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-display text-xl font-semibold text-[#121417]">{l.name}</h3>
          <span className="shrink-0 rounded-full border border-[#D7D2C8] bg-[#F4F1EA] px-2.5 py-1 font-mono-idb text-[11px] text-[#454B52]" data-testid={`lender-card-${l.id}-amount`}>{l.amountRange}</span>
        </div>
        <p className="mt-2 text-sm leading-relaxed text-[#454B52]">{l.pitch}</p>
        <ul className="mt-4 space-y-2 flex-1" data-testid={`lender-card-${l.id}-bullets`}>
          {l.bullets.map((b) => (
            <li key={b} className="flex items-start gap-2 text-sm leading-snug text-[#454B52]"><Check className="h-4 w-4 mt-0.5 shrink-0 text-[#2F6B57]" aria-hidden="true" /> {b}</li>
          ))}
        </ul>
        <a
          href={l.url}
          target="_blank"
          rel="noopener sponsored"
          onClick={() => trackFinancingClick(l.id, placement)}
          data-testid={`lender-card-${l.id}-button`}
          data-lender={l.id}
          className={`mt-5 h-12 w-full px-4 inline-flex items-center justify-center gap-2 rounded-[10px] text-sm font-semibold transition-colors active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3E6F8E] focus-visible:ring-offset-2 ${BTN.light[i % 4]}`}
        >
          {l.buttonLabel} <ArrowUpRight className="h-4 w-4 shrink-0 opacity-80" aria-hidden="true" />
        </a>
      </article>
    ))}
  </div>
);

/** "The facts, in one place": a table with one column per lender on desktop, stacked cards on mobile. */
export const LenderFactsTable = ({ lenders = LENDERS, testId = 'lender-facts' }) => (
  <div data-testid={testId} data-lender-count={lenders.length}>
    {/* desktop / tablet: table */}
    <div className="hidden md:block overflow-hidden rounded-[14px] border border-[#D7D2C8] bg-[#FFFCF6]">
      <table className="w-full border-separate border-spacing-0 text-sm" data-testid={`${testId}-table`}>
        <caption className="sr-only">Financing partner comparison</caption>
        <thead>
          <tr className="bg-[#F4F1EA] text-left">
            <th scope="col" className="px-4 py-3 text-xs font-medium uppercase tracking-[0.12em] text-[#61676E] w-[22%]">&nbsp;</th>
            {lenders.map((l) => <th key={l.id} scope="col" className="px-4 py-3 font-display text-base font-semibold text-[#121417]" data-testid={`${testId}-col-${l.id}`}>{l.name}</th>)}
          </tr>
        </thead>
        <tbody>
          {FACT_ROWS.map(([key, label]) => (
            <tr key={key} data-testid={`${testId}-row-${key}`}>
              <th scope="row" className="px-4 py-3 border-t border-[#E8E3D9] text-left text-xs font-semibold text-[#121417] align-top">{label}</th>
              {lenders.map((l) => <td key={l.id} className="px-4 py-3 border-t border-[#E8E3D9] text-sm leading-snug text-[#454B52] align-top">{l.facts[key] || '—'}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    {/* mobile: one stacked card per lender */}
    <div className="md:hidden space-y-4" data-testid={`${testId}-stacked`}>
      {lenders.map((l) => (
        <div key={l.id} className="rounded-[14px] border border-[#D7D2C8] bg-[#FFFCF6] p-4" data-testid={`${testId}-stack-${l.id}`}>
          <p className="font-display text-base font-semibold text-[#121417]">{l.name}</p>
          <dl className="mt-3 space-y-2.5">
            {FACT_ROWS.map(([key, label]) => (
              <div key={key} className="grid grid-cols-[42%,1fr] gap-3">
                <dt className="text-xs font-semibold text-[#121417]">{label}</dt>
                <dd className="text-sm leading-snug text-[#454B52]">{l.facts[key] || '—'}</dd>
              </div>
            ))}
          </dl>
        </div>
      ))}
    </div>
  </div>
);

/** The full disclosure in small type. */
export const FinancingDisclosure = ({ className = '', testId = 'financing-disclosure-text' }) => (
  <p className={`text-[11px] leading-relaxed text-[#61676E] ${className}`} data-testid={testId}>{FINANCING_DISCLOSURE}</p>
);

/** "How financing works" link used next to button pairs. */
export const FinancingLink = ({ className = '', dark = false, testId = 'financing-how-link' }) => (
  <Link to="/financing" className={`inline-flex items-center gap-1 text-xs font-medium underline-offset-2 hover:underline max-md:min-h-11 ${dark ? 'text-[#7FA7B8] hover:text-white' : 'text-[#2F5E78] hover:text-[#121417]'} ${className}`} data-testid={testId}>
    How financing works <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
  </Link>
);
